import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useEffect, useState } from 'react';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { CampaignGroupForm } from '../../../modules/components/forms/campaign-group/campaign-group.form';
import { observer } from 'mobx-react';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import styles from './edit-campaign-group.module.scss';
import { useCampaignGroupGet } from '../../../modules/hooks/apis/use-campaign-group-get';
import { SpacerX } from '@monorepo/base/src/components/spacer/spacer';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import { SiderSlider } from '@monorepo/base/src/components/sider-slider/sider-slider';
import routes from '../../../modules/routes/index.routes';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { BarLoader } from '@monorepo/base/src/components/bar-loader/bar-loader';
import { route } from '@monorepo/tools/src/lib/types/url';
import { IAdminxStore } from '../../../modules/stores';
import { Statuses } from '../../../modules/enums/status';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';

interface IEditCampaignGroups {
	parentRoute?: route;
}

export const EditCampaignGroups = observer((props: IEditCampaignGroups) => {
	const { parentRoute } = props;
	const { campaignGroupsStore } = useStores<IAdminxStore>();
	const { dispatchLog } = useLogs();
	const { advertiserId } = useParams();

	const [isFormClicked, setFormClicked] = useState<boolean>(false);
	const campaignGroupsCrudStore = campaignGroupsStore.getCrud();
	const campaignGroup = campaignGroupsCrudStore.getData();
	const name = campaignGroup.getName();

	const navigate = useNavigateParams();
	const location = useLocation();
	const params = useParams();

	useEffect(() => {
		campaignGroupsCrudStore.reset();
		campaignGroupsCrudStore.setIsLoading(true);
	}, [location]);

	if (params.campaignGroupId) {
		useCampaignGroupGet({ id: params.campaignGroupId });
	}

	const formStore = campaignGroupsCrudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isLoading = campaignGroupsCrudStore.getIsLoading();
	const isSuccess = campaignGroupsCrudStore.getIsSuccess();
	const error = campaignGroupsCrudStore.getHttpError();

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		if (isSuccess) {
			navigate(generatePath(parentRoute || routes.campaignGroups.index(), { advertiserId }), {
				state: { isBlockRequset: false } as BetweenPagesStateProps,
			});
			dispatchLog({
				msg: `Campaign Group ${name} was successfully edited`,
				type: DispatchLogsTypes.Success,
			});
			campaignGroupsCrudStore.setIsSuccess(false);
		}
	}, [isSuccess]);

	const onEditCampaignGroup = () => {
		setFormClicked(true);
		campaignGroupsStore.getCrud().edit();
	};

	const onCloseSiderSlider = () => {
		navigate(generatePath(parentRoute || routes.campaignGroups.index(), { advertiserId }), {
			state: { isBlockRequest: true } as BetweenPagesStateProps,
		});
	};

	return (
		<SiderSlider onClose={() => onCloseSiderSlider()} isOpen={true}>
			<BarLoader is={isLoading} />
			<Disable is={isLoading}>
				<Page>
					<Pagebar>
						<PagebarTitle unstyled={true}>
							<div className={styles.titleWrapper}>
								<div className={styles.title}>Edit Campaign Group</div>
								<div className={styles.campaignStatus}>
									<span>{isLoading ? '...' : name}</span>
									<SpacerX />
									<Toggle
										label={campaignGroup.getStatus() === Statuses.Active ? Statuses.Active : Statuses.Paused}
										onChange={status => campaignGroup.setStatus(status ? Statuses.Active : Statuses.Paused)}
										defaultValue={campaignGroup.getStatus() === Statuses.Active}
										debugProps={{ dataAttrs: [new DataAttribute('id', 'campaign_group_status')] }}
									/>
								</div>
							</div>
						</PagebarTitle>
					</Pagebar>
					<CampaignGroupForm />
				</Page>
				<BottomBar>
					{error ? <ErrorMsg error={error} /> : null}
					<BottomBarActions>
						<PrimaryButton
							onClick={() => onEditCampaignGroup()}
							isRotateIcon={isLoading}
							debugProps={{ dataAttrs: [new DataAttribute('id', 'save_campaign_group_button')] }}
							icon={isLoading ? 'loading-01' : 'send-03'}>
							Save
						</PrimaryButton>
					</BottomBarActions>
				</BottomBar>
			</Disable>
		</SiderSlider>
	);
});
